import { clamp01, frac } from '../../shared/scalar';
import { hash } from '../hash';
import { easeOutCubic } from '../motion';

const TAU = Math.PI * 2;

// His window follows the sim's `beatArmor`.
export interface Cadence {
  readonly cycleBeats: number;
  readonly openBeats: number;
}

function intoCycle(beats: number, cadence: Cadence): number {
  const { cycleBeats } = cadence;
  return ((beats % cycleBeats) + cycleBeats) % cycleBeats;
}

export function windowOpen(beats: number, cadence: Cadence): boolean {
  return intoCycle(beats, cadence) < cadence.openBeats;
}

const LIGHT = { attack: 0.08, release: 0.2 };

export function columnLight(beats: number, cadence: Cadence): number {
  const into = intoCycle(beats, cadence);
  return into < cadence.openBeats
    ? clamp01(into / LIGHT.attack)
    : clamp01(1 - (into - cadence.openBeats) / LIGHT.release);
}

export function windowFill(beats: number, cadence: Cadence): number {
  const into = intoCycle(beats, cadence);
  const { cycleBeats, openBeats } = cadence;
  return into < openBeats ? 1 : (into - openBeats) / (cycleBeats - openBeats);
}

const MOUTH = { shut: 6, open: 4 };

export function mouthOpen(beats: number): number {
  return MOUTH.shut + MOUTH.open * Math.exp(-frac(beats) * 5);
}

// The floor monitor's cone kicks with the Larsen's cry, on each beat, and rests in calm mode.
export function conePump(beats: number, still: boolean): number {
  return still ? 0 : Math.exp(-frac(beats) * 5);
}

const GLINT_SPAN = 140;

export function glintX(loopShare: number): number {
  return loopShare * GLINT_SPAN - GLINT_SPAN / 2;
}

export const WAVE = {
  beats: 3,
  from: 1,
  to: 3.75,
  bristle: 0.11,
  lobes: 18,
  live: 3,
  calmBeats: 2,
  width: 1.5,
  goldWidth: 2.6,
};

export interface Wave {
  readonly radius: number;
  readonly bristle: number;
  readonly turn: number;
  readonly gold: boolean;
  readonly alpha: number;
}

export function wavesBorn(beats: number): number[] {
  return Array.from({ length: WAVE.live }, (_, index) => Math.floor(beats) - index);
}

export function wave(born: number, beats: number, still: boolean, gold: boolean): Wave | undefined {
  const p = (beats - born) / WAVE.beats;
  if (p < 0 || p >= 1) {
    return undefined;
  }
  return {
    radius: WAVE.from + (WAVE.to - WAVE.from) * p,
    bristle: still ? 0 : WAVE.bristle * (1 - p),
    turn: beats * 2 + born,
    gold,
    alpha: 0.6 * (1 - p),
  };
}

export function bristlePoint(
  step: number,
  steps: number,
  radius: number,
  bristle: number,
  turn: number,
): readonly [number, number] {
  const angle = (step / steps) * TAU;
  const reach = radius + bristle * Math.sin(angle * WAVE.lobes + turn);
  return [Math.cos(angle) * reach, Math.sin(angle) * reach];
}

export const SHARDS = {
  count: 8,
  calmCount: 4,
  beats: 0.8,
  reach: 1.05,
  fall: 0.35,
  ringBeats: 0.4,
  ringFrom: 0.75,
  ringTo: 2.25,
};

export interface Shard {
  readonly x: number;
  readonly y: number;
  readonly turn: number;
  readonly size: number;
  readonly alpha: number;
}

export function shard(index: number, p: number, seed: number): Shard {
  const angle = hash(seed, index) * TAU;
  const far = SHARDS.reach * easeOutCubic(p) * (0.5 + hash(seed, index + 20));
  return {
    x: Math.cos(angle) * far,
    y: Math.sin(angle) * far + p * p * SHARDS.fall,
    turn: p * 6 + index,
    size: 0.06 + hash(seed, index + 40) * 0.075,
    alpha: 1 - p,
  };
}

export function shardRing(sinceBeats: number): { radius: number; alpha: number } | undefined {
  const p = sinceBeats / SHARDS.ringBeats;
  if (p < 0 || p >= 1) {
    return undefined;
  }
  return { radius: SHARDS.ringFrom + (SHARDS.ringTo - SHARDS.ringFrom) * p, alpha: 1 - p };
}

export const SPARKS = { count: 3, beats: 0.6, from: 0.85, reach: 1, spread: 1.6 };

export interface Spark {
  readonly x: number;
  readonly y: number;
  readonly turn: number;
  readonly alpha: number;
}

export function spark(index: number, p: number, seed: number, toward: number): Spark {
  const turn = toward + (hash(seed, index + 60) - 0.5) * SPARKS.spread;
  const far = SPARKS.from + p * SPARKS.reach;
  return { x: Math.cos(turn) * far, y: Math.sin(turn) * far, turn, alpha: clamp01(1 - p) };
}

export const CABLE = { drop: 0.9, sag: 0.5, sway: 0.3 };

export function cableSag(beats: number, still: boolean): number {
  return still ? CABLE.sag : CABLE.sag * (1 + CABLE.sway * Math.sin(beats * 0.7));
}

export const EXIT_BEATS = 2;

export interface Whip {
  readonly pull: number;
  readonly lash: number;
  readonly alpha: number;
}

export function cableWhip(p: number): Whip {
  return { pull: easeOutCubic(p), lash: 3 * Math.sin(p * Math.PI), alpha: 1 - p };
}

export function exitRing(p: number): { radius: number; alpha: number } {
  return { radius: 1 + 4.5 * p, alpha: 1 - p };
}

export function noteRise(p: number): { y: number; alpha: number } {
  return { y: -1 - 1.5 * p, alpha: Math.sin(p * Math.PI) };
}
